import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { api } from '../lib/api.js'
import { useAuth } from '../hooks/useAuth.js'
import { dateTime, money, percent, PLATFORM_LABEL, PLATFORM_TONE } from '../lib/format.js'
import {
  Badge,
  Card,
  Delta,
  Empty,
  Menu,
  MenuItem,
  PageHead,
  SearchInput,
  Segmented,
  Stat,
  StatsSkeleton,
  Tabs,
} from '../components/ui.js'
import {
  ArrowRightIcon,
  BagIcon,
  ChartIcon,
  CodeIcon,
  ExternalIcon,
  GearIcon,
  PlugIcon,
  PlusIcon,
  RupeeIcon,
  ShopifyIcon,
  StoreIcon,
  TargetIcon,
  TrendUpIcon,
  WordPressIcon,
} from '../components/icons.js'

const PLATFORM_ICON = {
  shopify: <ShopifyIcon size={22} />,
  woocommerce: <WordPressIcon size={22} />,
  custom: <CodeIcon size={22} />,
} as const

const RANGES = [
  { key: '7d', label: '7D' },
  { key: '30d', label: '30D' },
  { key: '3m', label: '3M' },
  { key: '1y', label: '1Y' },
]

/** What `/v1/stores` answers with. */
interface Store {
  id: string
  name: string | null
  domain: string
  platform: 'shopify' | 'woocommerce' | 'custom'
  currency: string
  installedAt: string | null
  uninstalledAt: string | null
  status: 'connected' | 'disconnected'
  planName: string | null
  revenue: string
  orders: number
  sessions: number
  conversionRate: number | null
  revenueDelta: number | null
  ordersDelta: number | null
  conversionDelta: number | null
}

// ── All stores ──────────────────────────────────────────────────────────────

/**
 * Real stores with real numbers.
 *
 * Revenue counts settled orders and conversion is orders over checkout
 * sessions — the same definitions `dashboard/metrics.ts` uses, so this page and
 * the dashboard cannot disagree about what a store earned.
 */
export function StoresPage(): React.ReactNode {
  const { platform } = useParams<{ platform?: string }>()
  const [search, setSearch] = useState('')
  const [range, setRange] = useState('7d')
  const navigate = useNavigate()

  const filter = platform === 'wordpress' ? 'woocommerce' : platform

  const { data, isPending, error } = useQuery({
    queryKey: ['stores', range],
    queryFn: () => api<{ range: string; stores: Store[] }>('/v1/stores', { query: { range } }),
  })

  const stores = data?.stores ?? []

  const visible = stores.filter((store) => {
    if (filter && store.platform !== filter) return false
    if (!search.trim()) return true
    const needle = search.trim().toLowerCase()
    return (store.name ?? '').toLowerCase().includes(needle) || store.domain.toLowerCase().includes(needle)
  })

  const totals = {
    revenue: stores.reduce((sum, store) => sum + Number(store.revenue), 0),
    orders: stores.reduce((sum, store) => sum + store.orders, 0),
    sessions: stores.reduce((sum, store) => sum + store.sessions, 0),
    active: stores.filter((store) => store.status === 'connected').length,
  }

  const aov = totals.orders > 0 ? totals.revenue / totals.orders : 0
  const conversion = totals.sessions > 0 ? (totals.orders / totals.sessions) * 100 : null

  const counts = {
    all: stores.length,
    shopify: stores.filter((s) => s.platform === 'shopify').length,
    woocommerce: stores.filter((s) => s.platform === 'woocommerce').length,
    custom: stores.filter((s) => s.platform === 'custom').length,
  }

  return (
    <>
      <PageHead
        title={filter ? `${PLATFORM_LABEL[filter]} stores` : 'All stores'}
        subtitle="Manage every connected store from one place."
        actions={
          <>
            <Segmented label="Date range" value={range} onChange={setRange} options={RANGES} />
            <Link to="/integrations" className="btn btn--primary">
              <PlusIcon size={16} />
              Add store
            </Link>
          </>
        }
      />

      {error && (
        <Card>
          <Empty error title="Could not load your stores">
            {(error as Error).message}
          </Empty>
        </Card>
      )}

      {isPending && <StatsSkeleton count={5} />}

      {data && (
        <section className="grid grid--4">
          <Stat
            label="Total stores"
            value={stores.length}
            icon={<StoreIcon size={20} />}
            tone="var(--brand)"
            footnote={<span className="faint">{totals.active} connected</span>}
          />
          <Stat label="Revenue" value={money(totals.revenue)} icon={<RupeeIcon size={20} />} tone="var(--ok)" />
          <Stat label="Orders" value={totals.orders.toLocaleString('en-IN')} icon={<BagIcon size={20} />} tone="var(--info)" />
          <Stat
            label="Conversion"
            value={conversion === null ? '—' : percent(conversion)}
            icon={<TargetIcon size={20} />}
            tone="var(--warn)"
            footnote={<span className="faint">{totals.sessions.toLocaleString('en-IN')} checkouts opened</span>}
          />
          <Stat
            label="Average order value"
            value={totals.orders > 0 ? money(aov) : '—'}
            icon={<TrendUpIcon size={20} />}
            tone="var(--accent)"
          />
        </section>
      )}

      <Card flush>
        <Tabs
          value={filter ?? 'all'}
          onChange={(key) => navigate(key === 'all' ? '/stores' : `/stores/${key === 'woocommerce' ? 'wordpress' : key}`)}
          tabs={[
            { key: 'all', label: 'All stores', count: counts.all },
            { key: 'shopify', label: 'Shopify', count: counts.shopify },
            { key: 'woocommerce', label: 'WordPress', count: counts.woocommerce },
            { key: 'custom', label: 'Custom', count: counts.custom },
          ]}
        />
        <div className="toolbar">
          <SearchInput value={search} onChange={setSearch} placeholder="Search stores…" />
        </div>
      </Card>

      {data && visible.length === 0 ? (
        <Card>
          <Empty
            icon={<StoreIcon size={24} />}
            title={stores.length === 0 ? 'No stores connected yet' : 'No stores match those filters'}
            action={
              stores.length === 0 ? (
                <Link to="/integrations" className="btn btn--primary">
                  Connect your first store
                </Link>
              ) : undefined
            }
          >
            {stores.length === 0
              ? 'Connect a Shopify, WooCommerce or custom storefront to start taking orders through Outfalak Checkout.'
              : 'Try clearing the search box or switching tabs.'}
          </Empty>
        </Card>
      ) : (
        <section className="grid grid--3">
          {visible.map((store) => (
            <StoreCard key={store.id} store={store} />
          ))}
        </section>
      )}

      <Card title="Add a new store" subtitle="Connect another storefront to this account.">
        <div className="grid grid--3">
          <PlatformCard
            platform="shopify"
            title="Shopify Store"
            description="Connect your Shopify store in just a few clicks."
            to="/integrations/shopify"
          />
          <PlatformCard
            platform="woocommerce"
            title="WordPress / WooCommerce"
            description="Install our plugin and connect your WordPress store."
            to="/integrations/wordpress"
          />
          <PlatformCard
            platform="custom"
            title="Custom Website"
            description="Integrate using the JavaScript SDK or the REST API."
            to="/integrations/custom"
          />
        </div>
      </Card>
    </>
  )
}

function StoreCard({ store }: { store: Store }): React.ReactNode {
  const queryClient = useQueryClient()
  const { refresh } = useAuth()

  // Uninstalls the app on Shopify too — see `POST /v1/stores/:id/disconnect`.
  const disconnect = useMutation({
    mutationFn: () => api<{ id: string; disconnected: boolean }>(`/v1/stores/${store.id}/disconnect`, { method: 'POST' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['stores'] })
      void refresh()
    },
    onError: (error) => window.alert((error as Error).message),
  })

  const canDisconnect = store.platform === 'shopify' && store.status === 'connected'

  return (
    <article className="card store-card" style={{ ['--tone' as string]: PLATFORM_TONE[store.platform] }}>
      <div className="store-card__head">
        <span className="store-card__logo">{PLATFORM_ICON[store.platform]}</span>
        <span className="store-card__title">
          <strong>{store.name ?? store.domain}</strong>
          <a href={`https://${store.domain}`} target="_blank" rel="noreferrer noopener">
            {store.domain}
            <ExternalIcon size={12} />
          </a>
        </span>
        <Badge tone={store.status}>{store.status === 'connected' ? 'Connected' : 'Disconnected'}</Badge>
        <Menu label={`Actions for ${store.name ?? store.domain}`} trigger={<GearIcon size={16} />}>
          <MenuItem icon={<GearIcon size={16} />}>Store settings</MenuItem>
          <MenuItem icon={<ChartIcon size={16} />}>View analytics</MenuItem>
          <MenuItem icon={<ExternalIcon size={16} />} onClick={() => window.open(`https://${store.domain}`, '_blank', 'noopener')}>
            Open storefront
          </MenuItem>
          {canDisconnect && (
            <MenuItem
              danger
              icon={<PlugIcon size={16} />}
              onClick={() => {
                if (
                  !disconnect.isPending &&
                  window.confirm(
                    `Disconnect ${store.domain}? Outfalak Checkout is uninstalled from the store and its Buy Now button stops working. Orders and settings are kept; reinstall the app to reconnect.`,
                  )
                ) {
                  disconnect.mutate()
                }
              }}
            >
              {disconnect.isPending ? 'Disconnecting…' : 'Disconnect store'}
            </MenuItem>
          )}
        </Menu>
      </div>

      <div className="store-card__meta">
        <dl className="kv">
          <div className="kv__row">
            <dt>Platform</dt>
            <dd>{PLATFORM_LABEL[store.platform] ?? store.platform}</dd>
          </div>
          <div className="kv__row">
            <dt>Connected on</dt>
            <dd>{store.installedAt ? dateTime(store.installedAt) : '—'}</dd>
          </div>
          {store.planName && (
            <div className="kv__row">
              <dt>Plan</dt>
              <dd>{store.planName}</dd>
            </div>
          )}
          <div className="kv__row">
            <dt>Currency</dt>
            <dd>{store.currency}</dd>
          </div>
        </dl>
      </div>

      <div className="store-card__stats">
        <div className="store-card__stat">
          <small>Revenue</small>
          <strong>{money(store.revenue, store.currency)}</strong>
          <Delta value={store.revenueDelta} />
        </div>
        <div className="store-card__stat">
          <small>Orders</small>
          <strong>{store.orders.toLocaleString('en-IN')}</strong>
          <Delta value={store.ordersDelta} />
        </div>
        <div className="store-card__stat">
          <small>Conv. rate</small>
          {/* Null conversion means nobody opened the checkout in this window —
              which is not the same as 0% and must not read like it. */}
          <strong>{store.conversionRate === null ? '—' : percent(store.conversionRate)}</strong>
          <Delta value={store.conversionDelta} />
        </div>
      </div>

      <div className="store-card__actions">
        <a href={`https://${store.domain}`} target="_blank" rel="noreferrer noopener" className="btn btn--primary">
          <ExternalIcon size={15} />
          Open store
        </a>
        <Link to="/settings" className="btn">
          <GearIcon size={15} />
          Manage store
        </Link>
        <Link to="/analytics" className="btn">
          <ChartIcon size={15} />
          View analytics
        </Link>
      </div>
    </article>
  )
}

function PlatformCard({
  platform,
  title,
  description,
  to,
}: {
  platform: keyof typeof PLATFORM_ICON
  title: string
  description: string
  to: string
}): React.ReactNode {
  return (
    <div className="platform-card" style={{ ['--tone' as string]: PLATFORM_TONE[platform] }}>
      <span className="platform-card__logo">{PLATFORM_ICON[platform]}</span>
      <h3>{title}</h3>
      <p className="muted" style={{ fontSize: '.85rem' }}>
        {description}
      </p>
      <Link to={to} className="btn">
        Connect
        <ArrowRightIcon size={15} />
      </Link>
    </div>
  )
}
